import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";

import { useApi } from "../../lib/useApi";
import { imageResources } from "./imageResources";
import { useManualGeneration, useManualOwner } from "./manualHooks";
import type { ManualModule } from "./types";

export function AuthImage({ src, alt, module, view }: {
  src?: string; alt?: string; module: ManualModule; view: string;
}) {
  const api = useApi();
  const auth = useAuth();
  const [iss, sub] = useManualOwner();
  const generation = useManualGeneration();
  const [image, setImage] = useState<{ key: string; url: string }>();
  const allowed = typeof src === "string" &&
    new RegExp(`^/api/core/manual/${module}/img/[a-z0-9-]+\\.png$`).test(src);
  const owner = JSON.stringify([iss, sub, generation]);
  const key = JSON.stringify([owner, view, src]);

  useEffect(() => {
    if (!allowed || !src || !auth.isAuthenticated) return;
    let resource = imageResources.get(key);
    if (!resource) {
      const controller = new AbortController();
      resource = { owner, view, refs: 0, controller, promise: Promise.resolve("") };
      const current = resource;
      current.promise = api.manualImage(src, controller.signal).then((blob) => {
        if (controller.signal.aborted) throw new Error("画像の読み込みを中止した");
        current.url = URL.createObjectURL(blob);
        return current.url;
      });
      imageResources.set(key, current);
    }
    const current = resource;
    current.refs++;
    let active = true;
    void current.promise.then((url) => {
      if (active) setImage({ key, url });
    }).catch(() => {
      if (active) setImage(undefined);
    });
    return () => {
      active = false;
      if (--current.refs === 0) {
        current.controller.abort();
        if (current.url) URL.revokeObjectURL(current.url);
        if (imageResources.get(key) === current) imageResources.delete(key);
      }
    };
  }, [allowed, api, auth.isAuthenticated, auth.user?.access_token, key, owner, src, view]);

  if (!allowed) return null;
  return image?.key === key
    ? <img src={image.url} alt={alt ?? ""} className="manual-image" />
    : <span role="status">{alt ? `${alt}: ` : ""}画像を読み込み中</span>;
}
